import type { ILanguageResources, ITranslationModule } from '../../../interfaces';
import { interpolate } from '../../../global';

class Id {
  private constructor() {}
  public static readonly prefix = 'UI.scripts.fabricationJobs.FabricationJobActions.';
  public static readonly statusButton = `${Id.prefix}statusButton`;
  public static readonly confirmTransition = `${Id.prefix}confirmTransition`;
  public static readonly confirmInProduction = `${Id.prefix}confirmInProduction`;
  public static readonly confirmCompleted = `${Id.prefix}confirmCompleted`;
  public static readonly transitionedToast = `${Id.prefix}transitionedToast`;
  public static readonly deliveryDateLabel = `${Id.prefix}deliveryDateLabel`;
  public static readonly deliveryNotesLabel = `${Id.prefix}deliveryNotesLabel`;
  public static readonly deliveryDialogTitle = `${Id.prefix}deliveryDialogTitle`;
  public static readonly deliveryViewTitle = `${Id.prefix}deliveryViewTitle`;
  public static readonly deliveryViewHint = `${Id.prefix}deliveryViewHint`;
  public static readonly deliverySelectAll = `${Id.prefix}deliverySelectAll`;
  public static readonly deliverySelectedCount = `${Id.prefix}deliverySelectedCount`;
  public static readonly deliveryNoItemsSelected = `${Id.prefix}deliveryNoItemsSelected`;
  public static readonly deliveryColItem = `${Id.prefix}deliveryColItem`;
  public static readonly deliveryColImage = `${Id.prefix}deliveryColImage`;
  public static readonly deliveryColLocation = `${Id.prefix}deliveryColLocation`;
  public static readonly deliveryColOptions = `${Id.prefix}deliveryColOptions`;
  public static readonly deliveryColTags = `${Id.prefix}deliveryColTags`;
  public static readonly deliveryColTracking = `${Id.prefix}deliveryColTracking`;
  public static readonly deliveryColQty = `${Id.prefix}deliveryColQty`;
  public static readonly deliveryRecordedToast = `${Id.prefix}deliveryRecordedToast`;
  public static readonly trackingReferenceMenuItem = `${Id.prefix}trackingReferenceMenuItem`;
  public static readonly trackingReferenceDialogTitle = `${Id.prefix}trackingReferenceDialogTitle`;
  public static readonly trackingReferenceLabel = `${Id.prefix}trackingReferenceLabel`;
  public static readonly trackingReferenceColumnTitle = `${Id.prefix}trackingReferenceColumnTitle`;
  public static readonly noPurchaseOrderTitle = `${Id.prefix}noPurchaseOrderTitle`;
  public static readonly noPurchaseOrderExplanation = `${Id.prefix}noPurchaseOrderExplanation`;
  public static readonly noPurchaseOrderQuestion = `${Id.prefix}noPurchaseOrderQuestion`;
  public static readonly noPurchaseOrderAssign = `${Id.prefix}noPurchaseOrderAssign`;
  public static readonly noPurchaseOrderProceed = `${Id.prefix}noPurchaseOrderProceed`;
  public static readonly noPurchaseOrderCancel = `${Id.prefix}noPurchaseOrderCancel`;
  public static readonly offerAwaitingOrderQuestion = `${Id.prefix}offerAwaitingOrderQuestion`;
}

export class LangFabricationJobActions {
  private constructor() {}
  public static statusButton(): string {
    return interpolate(Id.statusButton);
  }
  public static confirmTransition(stateName: string): string {
    return interpolate(Id.confirmTransition, { stateName });
  }
  public static confirmInProduction(itemCount: number): string {
    return interpolate(Id.confirmInProduction, { itemCount });
  }
  public static confirmCompleted(itemCount: number): string {
    return interpolate(Id.confirmCompleted, { itemCount });
  }
  public static transitionedToast(stateName: string): string {
    return interpolate(Id.transitionedToast, { stateName });
  }
  public static deliveryDateLabel(): string {
    return interpolate(Id.deliveryDateLabel);
  }
  public static deliveryNotesLabel(): string {
    return interpolate(Id.deliveryNotesLabel);
  }
  public static deliveryDialogTitle(): string {
    return interpolate(Id.deliveryDialogTitle);
  }
  public static deliveryViewTitle(): string {
    return interpolate(Id.deliveryViewTitle);
  }

  public static deliveryViewHint(): string {
    return interpolate(Id.deliveryViewHint);
  }
  public static deliverySelectAll(): string {
    return interpolate(Id.deliverySelectAll);
  }
  public static deliverySelectedCount(selected: number, total: number): string {
    return interpolate(Id.deliverySelectedCount, { selected, total });
  }
  public static deliveryNoItemsSelected(): string {
    return interpolate(Id.deliveryNoItemsSelected);
  }
  public static deliveryColItem(): string {
    return interpolate(Id.deliveryColItem);
  }
  public static deliveryColImage(): string {
    return interpolate(Id.deliveryColImage);
  }
  public static deliveryColLocation(): string {
    return interpolate(Id.deliveryColLocation);
  }
  public static deliveryColOptions(): string {
    return interpolate(Id.deliveryColOptions);
  }
  public static deliveryColTags(): string {
    return interpolate(Id.deliveryColTags);
  }
  public static deliveryColTracking(): string {
    return interpolate(Id.deliveryColTracking);
  }
  public static deliveryColQty(): string {
    return interpolate(Id.deliveryColQty);
  }
  public static deliveryRecordedToast(itemCount: number): string {
    return interpolate(Id.deliveryRecordedToast, { itemCount });
  }
  public static trackingReferenceMenuItem(): string {
    return interpolate(Id.trackingReferenceMenuItem);
  }
  public static trackingReferenceDialogTitle(): string {
    return interpolate(Id.trackingReferenceDialogTitle);
  }
  public static trackingReferenceLabel(): string {
    return interpolate(Id.trackingReferenceLabel);
  }
  public static trackingReferenceColumnTitle(): string {
    return interpolate(Id.trackingReferenceColumnTitle);
  }

  public static noPurchaseOrderTitle(): string {
    return interpolate(Id.noPurchaseOrderTitle);
  }

  public static noPurchaseOrderExplanation(): string {
    return interpolate(Id.noPurchaseOrderExplanation);
  }

  public static noPurchaseOrderQuestion(): string {
    return interpolate(Id.noPurchaseOrderQuestion);
  }

  public static noPurchaseOrderAssign(): string {
    return interpolate(Id.noPurchaseOrderAssign);
  }

  public static noPurchaseOrderProceed(): string {
    return interpolate(Id.noPurchaseOrderProceed);
  }

  public static noPurchaseOrderCancel(): string {
    return interpolate(Id.noPurchaseOrderCancel);
  }

  public static offerAwaitingOrderQuestion(): string {
    return interpolate(Id.offerAwaitingOrderQuestion);
  }
}

export class FabricationJobActionsModule implements ITranslationModule {
  registerItems(register: ILanguageResources) {
    register.add(
      Id.statusButton,
      'Status',
      'Footer drop-up button on the fabrication job that lists the legal next states'
    );
    register.add(
      Id.confirmTransition,
      'Change the job status to {{stateName}}?',
      'Confirmation before a job state change that does not alter any quote item status'
    );
    register.add(
      Id.confirmInProduction,
      'Start production on this job? {{itemCount}} quote item(s) will move to In Production.',
      'Confirmation before moving a job to In Production'
    );
    register.add(
      Id.confirmCompleted,
      'Complete this job? {{itemCount}} quote item(s) will move to Ready for Delivery.',
      'Confirmation before moving a job to Completed'
    );
    register.add(Id.transitionedToast, 'Job moved to {{stateName}}', 'Toast after a successful state transition');
    register.add(Id.deliveryDateLabel, 'Delivery Date', 'Date field in the record delivery dialog');
    register.add(Id.deliveryNotesLabel, 'Notes', 'Notes field in the record delivery dialog');
    register.add(Id.deliveryDialogTitle, 'Record Delivery', 'Title of the record delivery dialog');
    register.add(
      Id.deliveryViewTitle,
      'Delivery',
      'Title when viewing a delivery that has already been recorded (read-only)'
    );
    register.add(
      Id.deliveryViewHint,
      'View Delivery',
      'Hover hint on a Delivered badge, which opens the delivery read-only'
    );
    register.add(Id.deliverySelectAll, 'Select all', 'Ticks every item in the record delivery checklist');
    register.add(
      Id.deliverySelectedCount,
      '{{selected}} of {{total}} selected',
      'Count beside the select-all control in the record delivery checklist'
    );
    register.add(
      Id.deliveryNoItemsSelected,
      'Select at least one item to deliver.',
      'Validation shown when the record delivery checklist has nothing ticked'
    );
    register.add(Id.deliveryColItem, 'Item #', 'Column header in the record delivery checklist');
    register.add(Id.deliveryColImage, 'Image', 'Column header in the record delivery checklist');
    register.add(Id.deliveryColLocation, 'Location and Description', 'Column header in the record delivery checklist');
    register.add(Id.deliveryColOptions, 'Options', 'Column header in the record delivery checklist');
    register.add(Id.deliveryColTags, 'Tags', 'Column header in the record delivery checklist');
    register.add(Id.deliveryColTracking, 'Tracking Ref', 'Column header in the record delivery checklist');
    register.add(Id.deliveryColQty, 'Qty', 'Column header in the record delivery checklist');
    register.add(
      Id.deliveryRecordedToast,
      'Delivery recorded for {{itemCount}} item(s)',
      'Toast after a successful delivery'
    );
    register.add(
      Id.trackingReferenceMenuItem,
      'Set Tracking Reference',
      'Row hamburger action opening the tracking reference dialog'
    );
    register.add(
      Id.trackingReferenceDialogTitle,
      'Factory Tracking Reference',
      'Title of the tracking reference dialog'
    );
    register.add(Id.trackingReferenceLabel, 'Reference', 'Single input in the tracking reference dialog');
    register.add(Id.trackingReferenceColumnTitle, 'Tracking Ref', 'Job items table column header');
    register.add(Id.noPurchaseOrderTitle, 'No Purchase Order', 'Title of the no-purchase-order prompt');
    register.add(
      Id.noPurchaseOrderExplanation,
      'This job is not on a purchase order yet, so nothing has been ordered for it.',
      'Explanation line of the no-purchase-order prompt'
    );
    register.add(
      Id.noPurchaseOrderQuestion,
      'What would you like to do?',
      'Question line of the no-purchase-order prompt'
    );
    register.add(Id.noPurchaseOrderAssign, 'Add to a Purchase Order', 'No-purchase-order prompt: pick or create a PO');
    register.add(
      Id.noPurchaseOrderProceed,
      'Continue Without One',
      'No-purchase-order prompt: change the state anyway'
    );
    register.add(Id.noPurchaseOrderCancel, 'Cancel', 'No-purchase-order prompt: abandon the state change');
    register.add(
      Id.offerAwaitingOrderQuestion,
      'This job is now on a purchase order. Move it to Awaiting Order?',
      'Asks whether to advance a Planned job after it is put on a purchase order'
    );
  }
}
